import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  MenuItem
} from '@material-ui/core';

import NotyHelper from '../api/NotyHelper';

const API_URL = process.env.REACT_APP_API_URL;

const getAccessToken = () => {
  try {
    return JSON.parse(JSON.parse(localStorage.getItem('persist:root')).auth).accessToken;
  } catch (e) {
    return null;
  }
};

// Raw fetch (not ApiClient) because the portal intake returns 201 on create and
// ApiClient.fetchJson treats anything other than 200 as an error.
const WarehouseDialog = ({ open, onClose, deviceId, onSuccess }) => {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    fetch(`${API_URL}devices/warehouses/`, {
      headers: { Authorization: `Bearer ${getAccessToken()}` }
    })
      .then((response) => {
        if (!response.ok) throw new Error('Failed to load warehouses');
        return response.json();
      })
      .then((data) => {
        if (!cancelled) setWarehouses(data);
      })
      .catch((error) => {
        console.error('Load warehouses error:', error);
        if (!cancelled) NotyHelper.error('Failed to load warehouses.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  return (
    <Formik
      enableReinitialize
      initialValues={{ warehouse: '' }}
      validationSchema={Yup.object().shape({
        warehouse: Yup.number().integer().required('Warehouse is required')
      })}
      onSubmit={async (values, { setSubmitting, resetForm }) => {
        try {
          const response = await fetch(
            `${API_URL}devices/${deviceId}/send-to-warehouse/`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${getAccessToken()}`
              },
              body: JSON.stringify({ warehouse: values.warehouse })
            }
          );
          if (!response.ok) {
            let detail = 'Failed to send device to warehouse.';
            try {
              const body = await response.json();
              if (body && body.detail) detail = body.detail;
            } catch (e) {
              /* non-JSON error body — keep the generic message */
            }
            throw new Error(detail);
          }
          const wh = warehouses.find((w) => w.id === Number(values.warehouse));
          NotyHelper.success(
            `Sent to warehouse ${wh ? wh.name : ''} · ${moment().format(
              'YYYY-MM-DD HH:mm'
            )}`
          );
          resetForm();
          onClose();
          if (onSuccess) onSuccess();
        } catch (error) {
          console.error('Send to warehouse error:', error);
          NotyHelper.error(error.message || 'Failed to send device to warehouse.');
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, handleChange, handleSubmit, isSubmitting }) => (
        <Dialog
          open={open}
          onClose={(_, reason) => {
            if (isSubmitting && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
            onClose();
          }}
          fullWidth
          maxWidth="xs"
        >
          <DialogContent>
            <form onSubmit={handleSubmit} autoComplete="off">
              <TextField
                id="select-warehouse"
                select
                fullWidth
                label="Warehouse"
                variant="outlined"
                margin="normal"
                name="warehouse"
                value={values.warehouse}
                onChange={handleChange}
                disabled={isSubmitting || loading}
                error={touched.warehouse && Boolean(errors.warehouse)}
                helperText={touched.warehouse && errors.warehouse}
              >
                <MenuItem value="">
                  <em>{loading ? 'Loading…' : 'None'}</em>
                </MenuItem>
                {warehouses.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.code} — {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </form>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} color="primary" disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              autoFocus
              disabled={isSubmitting || loading}
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {isSubmitting ? 'Sending…' : 'Send'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Formik>
  );
};

export default WarehouseDialog;
